import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateQuote, QuoteDto, QuoteList, QuoteResponse } from '@mys/contracts';
import { PLAN_PRICES_INR, PROJECT_PLANS } from '@mys/contracts/constants';
import { api, apiUrl } from './api.js';

/* ============================================================================
   Quotes, from the lead's screen.
   ========================================================================== */

export const QUOTE_PLANS = PROJECT_PLANS;

/** List price, so the form can prefill. Still sent explicitly — a quote is
    where a discount happens, and a silently derived price hides it. */
export const QUOTE_PLAN_PRICES = PLAN_PRICES_INR;

export function useQuotes(leadId: string) {
  return useQuery({
    queryKey: ['quotes', leadId],
    queryFn: ({ signal }) => api.get<QuoteList>(`/api/quotes/lead/${leadId}`, signal),
  });
}

export function useRaiseQuote(leadId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQuote) => api.post<QuoteResponse>(`/api/quotes/${leadId}`, input),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['quotes', leadId] }),
  });
}

export function useSendQuote(leadId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<QuoteResponse & { alreadySent: boolean }>(`/api/quotes/${id}/send`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['quotes', leadId] });
      /* Sending a quote is how a lead becomes "quoted", and the board's counts
         are on the leads screen. */
      void client.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/** Where the PDF lives. A plain link, not a fetch: it is behind the session
    cookie, so the browser opens it in its own viewer with no download plumbing
    of ours in between. */
export function quotePdfHref(id: string): string {
  return apiUrl(`/api/quotes/${id}/pdf`);
}

/**
 * How a quote reads on screen, in one word.
 *
 * Three states, not two. "Sent and still valid" and "sent but expired" are the
 * difference between a price somebody can still accept and one they cannot,
 * and a list that only knows sent/unsent makes the owner do that arithmetic
 * against today's date every time they look.
 */
export type QuoteState = 'expired' | 'sent' | 'draft';

export function quoteState(quote: QuoteDto): QuoteState {
  if (quote.expired) return 'expired';
  return quote.sentAt ? 'sent' : 'draft';
}

export const QUOTE_STATE_LABELS: Record<QuoteState, string> = {
  expired: 'Expired',
  sent: 'Sent',
  draft: 'Draft',
};
